import { makeAutoObservable, runInAction } from './mobx-config';
import contextStore from './contextStore';
import { logger } from '../../utils/debug/logger.js';
import { validateUserData, validatePartnerData, validateAuthData } from './authValidator.js';
import { trackAuthEvent, AUTH_EVENT_TYPES } from './authMonitoring.js';

// Constantes pour les types d'authentification
const AUTH_MODES = {
    IFRAME: 'iframe',
    PROXY: 'proxy',
    DIRECT: 'direct',
    // Invité authentifié via auth-service (cookie __authsvc sur .genealogie.app).
    // Aucun partenaire associé — l'identité provient du JWT auth-service uniquement.
    GUEST: 'guest'
};

// Origine auth-service pour le tenant geneafan (déclaré dans
// shared/auth-service/src/tenants.js → hostname = 'auth.genealogie.app').
const AUTHSVC_ORIGIN = 'https://auth.genealogie.app';

class AuthStore {
    userInfo = null;
    partnerInfo = null;
    isLoading = false;
    error = null;
    authMode = null; // Stocke explicitement le mode d'authentification actuel
    isAuthenticating = false; // Mutex pour éviter les appels simultanés
    _cachedUserData = null; // Cache temporaire pour éviter appels API redondants

    constructor() {
        makeAutoObservable(this);
    }

    setError(error) {
        runInAction(() => {
            this.error = error;
        });
    }

    setLoading(status) {
        runInAction(() => {
            this.isLoading = status;
        });
    }

    /**
     * Définit le mode d'authentification actuel
     * @param {string} mode Le mode d'authentification ('iframe', 'proxy', 'direct')
     */
    setAuthMode(mode) {
        runInAction(() => {
            this.authMode = mode;
            logger.auth.debug(`🔐 Mode d'authentification défini: ${mode}`);
        });
    }

    /**
     * Définit les informations partenaire
     * @param {Object} partnerInfo Informations du partenaire
     */
    setPartnerInfo(partnerInfo) {
        runInAction(() => {
            this.partnerInfo = partnerInfo;
            
            // Rendre les infos partenaire accessibles via window.__PARTNER__ pour compatibilité
            window.__PARTNER__ = partnerInfo;
        });
        
        logger.auth.debug('✅ Informations partenaire enregistrées:', this.partnerInfo);
    }

    /**
     * Initialise l'authentification complète (partner + user)
     * Point d'entrée unique pour l'authentification
     * Protégé contre les appels simultanés par un mutex
     */
    async checkAuthentication() {
        // Mutex: Si une authentification est déjà en cours, attendre qu'elle se termine
        if (this.isAuthenticating) {
            logger.auth.debug('⏳ Authentification déjà en cours, attente...');

            // Tracer la race condition détectée
            trackAuthEvent(AUTH_EVENT_TYPES.RACE_CONDITION_DETECTED, {
                timestamp: Date.now()
            });

            // Attendre que l'authentification en cours se termine
            return new Promise((resolve) => {
                const checkInterval = setInterval(() => {
                    if (!this.isAuthenticating) {
                        clearInterval(checkInterval);
                        logger.auth.debug('✅ Authentification précédente terminée, utilisation du résultat');
                        resolve();
                    }
                }, 100);
            });
        }

        logger.auth.debug('🔍 Vérification de l\'authentification en cours...');

        // Déterminer le mode d'authentification
        const mode = this.determineAuthMode();

        // Tracer le début de l'authentification
        trackAuthEvent(AUTH_EVENT_TYPES.INIT_START, { mode });

        runInAction(() => {
            this.isAuthenticating = true;
        });

        try {
            this.setLoading(true);

            this.setAuthMode(mode);
            logger.auth.debug(`🔍 Contexte d'authentification: ${mode}`);

            // ÉTAPE 0 (guest path): si aucun partenaire n'est attendu
            // (mode direct, pas d'iframe ni proxy), tenter d'abord de
            // résoudre une session auth-service. Si l'invité est signé
            // in via __authsvc, on saute toute initialisation partenaire.
            if (mode === AUTH_MODES.DIRECT) {
                const guestUser = await this.probeGuestSession();
                if (guestUser) {
                    runInAction(() => {
                        this.userInfo = guestUser;
                    });
                    this.setAuthMode(AUTH_MODES.GUEST);
                    logger.auth.debug('✅ Session invité auth-service détectée:', guestUser.email);
                    trackAuthEvent(AUTH_EVENT_TYPES.USER_AUTH_SUCCESS, {
                        mode: AUTH_MODES.GUEST,
                        userId: guestUser.id,
                        source: 'authsvc-me',
                    });
                    return;
                }
            }

            // ÉTAPE 1: Authentification du partenaire
            await this.initializePartnerAuthentication(mode);

            // ÉTAPE 2: Authentification de l'utilisateur selon le mode
            await this.authenticateUserByMode(mode);

            // Succès
            trackAuthEvent(AUTH_EVENT_TYPES.INIT_SUCCESS, {
                mode,
                hasUserInfo: !!this.userInfo,
                hasPartnerInfo: !!this.partnerInfo
            });

        } catch (error) {
            logger.auth.error('❌ Exception lors de la vérification de l\'authentification:', error);
            this.setError(error);

            // Tracer l'échec
            trackAuthEvent(AUTH_EVENT_TYPES.INIT_FAILURE, {
                mode,
                error: error.message
            });

            throw error; // Relancer l'erreur pour qu'elle remonte

        } finally {
            this.setLoading(false);
            runInAction(() => {
                this.isAuthenticating = false;
            });
            logger.auth.debug('🏁 Vérification de l\'authentification terminée - État actuel:', this.isAuthenticated ? 'Authentifié' : 'Non authentifié');
        }
    }
    
    /**
     * Détermine le mode d'authentification en fonction du contexte
     * @returns {string} Le mode d'authentification ('iframe', 'proxy', 'direct')
     */
    determineAuthMode() {
        // Si le mode est déjà défini et que nous sommes dans une requête suivante, l'utiliser
        if (this.authMode) {
            return this.authMode;
        }

        // /guest/ landing : surface dédiée aux invités via __authsvc.
        // On force DIRECT (avant la détection isProxied) pour garantir
        // que probeGuestSession soit tenté en priorité, indépendamment
        // de cookies partenaires éventuellement stale.
        try {
            if (window.location.pathname.startsWith('/guest/')) {
                return AUTH_MODES.DIRECT;
            }
        } catch (_) { /* SSR or restricted env */ }

        // Sinon déterminer le mode à partir du contexte
        const { isInIframe, isProxied } = contextStore;

        if (isInIframe) {
            return AUTH_MODES.IFRAME;
        } else if (isProxied) {
            return AUTH_MODES.PROXY;
        } else {
            return AUTH_MODES.DIRECT;
        }
    }

    /**
     * Authentifie l'utilisateur en fonction du mode
     * @param {string} mode Le mode d'authentification
     */
    async authenticateUserByMode(mode) {
        switch (mode) {
            case AUTH_MODES.IFRAME:
                if (contextStore.hasAuthParams()) {
                    await this.authenticateFromIframeParams();
                } else {
                    await this.authenticateFromApi();
                }
                break;
            
            case AUTH_MODES.PROXY:
            case AUTH_MODES.DIRECT:
            default:
                await this.authenticateFromApi();
                break;
        }
    }

    /**
     * Initialise l'authentification partenaire selon le mode
     * @param {string} mode Le mode d'authentification
     */
    async initializePartnerAuthentication(mode) {
        // Si le partenaire est déjà défini, ne rien faire
        if (window.__PARTNER__) {
            logger.auth.debug('⏩ window.__PARTNER__ déjà initialisé, utilisation des valeurs existantes');
            // Assurez-vous que le mode d'authentification est correctement défini
            if (!window.__PARTNER__.authMode) {
                window.__PARTNER__.authMode = mode;
            }
            this.setPartnerInfo(window.__PARTNER__);
            return;
        }

        switch (mode) {
            case AUTH_MODES.IFRAME:
                await this.initializePartnerForIframe();
                break;
                
            case AUTH_MODES.PROXY:
            case AUTH_MODES.DIRECT:
                await this.initializePartnerForServerApi(mode);
                break;
                
            default:
                logger.auth.warn(`⚠️ Mode d'authentification non reconnu: ${mode}`);
                break;
        }
    }
    
    /**
     * Initialise le partenaire pour le mode iframe
     */
    async initializePartnerForIframe() {
        if (contextStore.hasAuthParams()) {
            logger.auth.debug('🔄 Contexte iframe: initialisation partenaire depuis les paramètres d\'URL pré-validés');

            trackAuthEvent(AUTH_EVENT_TYPES.PARTNER_AUTH_START, { mode: 'iframe' });

            // Récupérer les paramètres nécessaires
            const authInfo = contextStore.getAuthFromUrl();
            const partnerId = contextStore.getUrlParam('partner_id') || '';
            const partnerToken = contextStore.getUrlParam('partner_token') || '';

            if (partnerId && partnerToken) {
                // Valider les données avant de les utiliser
                const validation = validatePartnerData({
                    id: partnerId,
                    token: partnerToken
                });

                if (!validation.isValid) {
                    // Données invalides ou corrompues
                    trackAuthEvent(AUTH_EVENT_TYPES.VALIDATION_ERROR, {
                        mode: 'iframe',
                        type: 'partner',
                        errors: validation.errors
                    });

                    throw new Error(
                        'Authentification partenaire échouée (mode iframe): ' +
                        'données invalides ou corrompues. ' +
                        `Erreurs: ${validation.errors.join(', ')}`
                    );
                }

                // La validation a déjà été effectuée par /iframe-entry/, utiliser directement les paramètres
                const partnerInfo = {
                    id: partnerId,
                    token: partnerToken,
                    userId: authInfo.id,
                    email: authInfo.email,
                    firstName: '',
                    lastName: '',
                    authMode: AUTH_MODES.IFRAME
                };

                this.setPartnerInfo(partnerInfo);
                logger.auth.debug('✅ Partenaire authentifié via iframe avec paramètres pré-validés');

                trackAuthEvent(AUTH_EVENT_TYPES.PARTNER_AUTH_SUCCESS, {
                    mode: 'iframe',
                    partnerId
                });
            } else {
                // Paramètres manquants : erreur critique qui doit remonter
                logger.auth.error('❌ Échec d\'authentification partenaire iframe - Paramètres manquants:', {
                    hasPartnerId: !!partnerId,
                    hasPartnerToken: !!partnerToken,
                    hasAuthInfo: !!authInfo
                });

                trackAuthEvent(AUTH_EVENT_TYPES.PARTNER_AUTH_FAILURE, {
                    mode: 'iframe',
                    reason: 'missing_parameters',
                    hasPartnerId: !!partnerId,
                    hasPartnerToken: !!partnerToken
                });

                throw new Error(
                    'Authentification partenaire échouée (mode iframe): ' +
                    'paramètres partner_id ou partner_token manquants dans l\'URL. ' +
                    'Vérifiez que le site partenaire transmet correctement les paramètres d\'authentification.'
                );
            }
        } else {
            logger.auth.debug('⚠️ Mode iframe sans paramètres d\'authentification, tentative d\'initialisation via API');
            await this.initializePartnerForServerApi(AUTH_MODES.IFRAME);
        }
    }
    
    /**
     * Initialise le partenaire pour les modes proxy ou direct via API
     * @param {string} mode Le mode d'authentification
     */
    async initializePartnerForServerApi(mode) {
        try {
            trackAuthEvent(AUTH_EVENT_TYPES.PARTNER_AUTH_START, { mode });

            // L'endpoint /proxy-entry est servi par le gateway sur le hostname courant,
            // que ce soit familystory.live (legacy) ou un custom hostname partenaire.
            // Toujours appeler en relatif : le gateway route via le Host header.
            const apiUrl = './proxy-entry';
            logger.auth.debug(`🔄 Récupération des infos partenaire via ${apiUrl}...`);

            const res = await fetch(apiUrl);
            if (process.env.NODE_ENV === 'development') {
                logger.auth.debug(`📊 Statut de la réponse auth: ${res.status}`);
            }

            if (res.ok) {
                const data = await res.json();
                logger.auth.debug('📦 Données reçues de /proxy-entry:', data);

                // Vérifier la présence de toutes les données requises
                if (data?.isAuthenticated && data?.partnerId && data?.partnerToken) {
                    // Valider les données avant de les utiliser
                    const validation = validateAuthData({
                        partnerId: data.partnerId,
                        partnerToken: data.partnerToken,
                        userId: data.userId,
                        email: data.email,
                        firstName: data.firstName,
                        lastName: data.lastName
                    });

                    if (!validation.isValid) {
                        // Données invalides ou corrompues
                        trackAuthEvent(AUTH_EVENT_TYPES.VALIDATION_ERROR, {
                            mode,
                            type: 'partner+user',
                            errors: validation.errors
                        });

                        throw new Error(
                            `Authentification partenaire échouée (mode ${mode}): ` +
                            'données invalides ou corrompues reçues de /proxy-entry. ' +
                            `Erreurs: ${validation.errors.join(', ')}`
                        );
                    }

                    const partnerInfo = {
                        id: data.partnerId,
                        token: data.partnerToken,
                        userId: data.userId,
                        email: data.email,
                        firstName: data.firstName || '',
                        lastName: data.lastName || '',
                        authMode: mode
                    };

                    this.setPartnerInfo(partnerInfo);
                    logger.auth.debug(`✅ Partenaire authentifié via ${mode}`);

                    trackAuthEvent(AUTH_EVENT_TYPES.PARTNER_AUTH_SUCCESS, {
                        mode,
                        partnerId: data.partnerId
                    });

                    // OPTIMISATION: Si les données utilisateur sont présentes, les mettre en cache
                    // pour éviter un second appel API dans authenticateFromApi()
                    if (data.userId && data.email) {
                        runInAction(() => {
                            this._cachedUserData = {
                                userId: data.userId,
                                email: data.email,
                                firstName: data.firstName || '',
                                lastName: data.lastName || ''
                            };
                        });
                        logger.auth.debug('💾 Données utilisateur mises en cache pour éviter appel API redondant');
                    }
                } else {
                    // Données incomplètes : erreur critique qui doit remonter
                    const errorDetails = {
                        hasPartnerId: !!data?.partnerId,
                        hasPartnerToken: !!data?.partnerToken,
                        isAuthenticated: !!data?.isAuthenticated,
                        mode: mode
                    };

                    logger.auth.error('❌ Échec d\'authentification partenaire - Données incomplètes:', errorDetails);

                    trackAuthEvent(AUTH_EVENT_TYPES.PARTNER_AUTH_FAILURE, {
                        mode,
                        reason: 'incomplete_data',
                        ...errorDetails
                    });

                    throw new Error(
                        `Authentification partenaire échouée (mode ${mode}): ` +
                        `données incomplètes reçues de /proxy-entry. ` +
                        `Vérifiez la configuration du reverse proxy et l'authentification Cloudflare Worker.`
                    );
                }
            } else {
                // Tracer l'erreur API
                trackAuthEvent(AUTH_EVENT_TYPES.API_ERROR, {
                    mode,
                    type: 'partner',
                    status: res.status,
                    url: apiUrl
                });

                // Meilleure gestion d'erreur
                try {
                    const errorJson = await res.json();
                    logger.auth.error('❌ Échec de récupération des infos partenaire:', res.status, errorJson);
                } catch {
                    // En développement seulement, pas d'erreur en production pour les 404 attendus
                    if (process.env.NODE_ENV === 'development' && res.status !== 404) {
                        const errorText = await res.text();
                        logger.auth.error('❌ Échec de récupération des infos partenaire:', res.status, errorText.substring(0, 100));
                    }
                }
            }
        } catch (err) {
            logger.auth.error('❌ Erreur lors de la récupération des infos partenaire:', err);

            trackAuthEvent(AUTH_EVENT_TYPES.PARTNER_AUTH_FAILURE, {
                mode,
                reason: 'exception',
                error: err.message
            });
        }
    }

    /**
     * Probe la présence d'une session auth-service (cookie __authsvc sur
     * .genealogie.app). Renvoie un objet userInfo si l'invité est signé in,
     * null sinon. Aucune redirection n'est déclenchée — c'est volontairement
     * silencieux pour permettre au flow normal (partenaire) de prendre le
     * relais quand il n'y a pas de session invitée.
     */
    async probeGuestSession() {
        try {
            const response = await fetch(`${AUTHSVC_ORIGIN}/me`, {
                method: 'GET',
                credentials: 'include',
                mode: 'cors',
            });
            if (!response.ok) {
                logger.auth.debug(`⚠️ /me status ${response.status} — pas de session invité`);
                return null;
            }
            const data = await response.json();
            if (!data?.authenticated || !data?.accountId || !data?.email) {
                return null;
            }
            return {
                id: data.accountId,
                email: data.email,
                fullName: data.email.split('@')[0],
                firstName: '',
                lastName: '',
                authMode: AUTH_MODES.GUEST,
            };
        } catch (err) {
            logger.auth.debug('⚠️ /me probe failed (network or CORS), ignoré:', err.message);
            return null;
        }
    }

    /**
     * Authentifie l'utilisateur à partir des paramètres d'URL iframe
     * Ces paramètres sont pré-validés par le système d'entrée iframe
     */
    async authenticateFromIframeParams() {
        const authInfo = contextStore.getAuthFromUrl();
        logger.auth.debug('🔑 Authentification iframe: informations trouvées dans l\'URL');

        trackAuthEvent(AUTH_EVENT_TYPES.USER_AUTH_START, { mode: 'iframe' });

        if (!authInfo || !authInfo.id || !authInfo.email) {
            logger.auth.error('❌ Informations d\'authentification iframe incomplètes ou invalides');

            trackAuthEvent(AUTH_EVENT_TYPES.USER_AUTH_FAILURE, {
                mode: 'iframe',
                reason: 'missing_auth_info',
                hasAuthInfo: !!authInfo,
                hasId: !!(authInfo?.id),
                hasEmail: !!(authInfo?.email)
            });

            return;
        }

        // Valider les données utilisateur
        const validation = validateUserData({
            id: authInfo.id,
            email: authInfo.email
        });

        if (!validation.isValid) {
            trackAuthEvent(AUTH_EVENT_TYPES.VALIDATION_ERROR, {
                mode: 'iframe',
                type: 'user',
                errors: validation.errors
            });

            logger.auth.error('❌ Données utilisateur invalides:', validation.errors);
            return;
        }

        // Mettre à jour l'état d'authentification
        runInAction(() => {
            this.userInfo = {
                id: authInfo.id,
                email: authInfo.email,
                fullName: authInfo.email.split('@')[0], // Fallback simple pour le nom
                authMode: AUTH_MODES.IFRAME
            };
        });

        logger.auth.debug('✅ Utilisateur authentifié via iframe:', this.userInfo);

        trackAuthEvent(AUTH_EVENT_TYPES.USER_AUTH_SUCCESS, {
            mode: 'iframe',
            userId: authInfo.id
        });
    }

    /**
     * Authentifie l'utilisateur via appel API
     * Cette méthode est utilisée dans tous les contextes lorsque nécessaire
     */
    async authenticateFromApi() {
        const mode = this.authMode;

        trackAuthEvent(AUTH_EVENT_TYPES.USER_AUTH_START, { mode });

        // OPTIMISATION: Si les données utilisateur sont en cache, les utiliser directement
        if (this._cachedUserData) {
            logger.auth.debug('⚡ Utilisation des données utilisateur en cache (pas d\'appel API)');

            const cachedData = this._cachedUserData;

            // Nettoyer le cache immédiatement
            runInAction(() => {
                this._cachedUserData = null;
            });

            // Valider les données du cache
            const validation = validateUserData({
                id: cachedData.userId,
                email: cachedData.email,
                firstName: cachedData.firstName,
                lastName: cachedData.lastName
            });

            if (validation.isValid) {
                // Utiliser les données du cache
                runInAction(() => {
                    this.userInfo = {
                        id: cachedData.userId,
                        email: cachedData.email,
                        fullName: cachedData.firstName || cachedData.lastName
                            ? `${cachedData.firstName || ''} ${cachedData.lastName || ''}`.trim()
                            : cachedData.email.split('@')[0],
                        firstName: cachedData.firstName || '',
                        lastName: cachedData.lastName || '',
                        authMode: mode
                    };
                });
                logger.auth.debug(`✅ Utilisateur authentifié depuis cache (${mode}):`, this.userInfo);

                trackAuthEvent(AUTH_EVENT_TYPES.USER_AUTH_SUCCESS, {
                    mode,
                    userId: cachedData.userId,
                    source: 'cache'
                });

                return; // ✅ Pas d'appel API nécessaire !
            } else {
                // Données du cache invalides (ne devrait pas arriver)
                logger.auth.warn('⚠️ Données du cache invalides, appel API nécessaire:', validation.errors);
            }
        }

        // Pas de cache ou cache invalide : faire l'appel API comme avant
        // Choisir l'API en fonction du mode
        let apiUrl;

        if (mode === AUTH_MODES.IFRAME) {
            // Dans le contexte iframe, utiliser /proxy-entry avec le Worker unifié
            apiUrl = '/proxy-entry';
        } else if (mode === AUTH_MODES.PROXY) {
            // Dans le contexte proxy, utiliser ./proxy-entry (relatif)
            apiUrl = './proxy-entry';
        } else {
            // Pour le mode direct, utiliser l'API d'authentification standard
            apiUrl = '/api/auth-info';
        }

        logger.auth.debug(`📡 Appel de l\'API auth-info à l\'URL '${apiUrl}' (mode ${mode})`);

        try {
            const response = await fetch(apiUrl);
            logger.auth.debug(`📥 Réponse reçue - Status: ${response.status}`);

            if (response.ok) {
                try {
                    const authData = await response.json();
                    logger.auth.debug('📦 Données d\'authentification:', authData);

                    if (authData.isAuthenticated && authData.userId && authData.email) {
                        // Valider les données utilisateur avant de les utiliser
                        const validation = validateUserData({
                            id: authData.userId,
                            email: authData.email,
                            firstName: authData.firstName,
                            lastName: authData.lastName
                        });

                        if (!validation.isValid) {
                            trackAuthEvent(AUTH_EVENT_TYPES.VALIDATION_ERROR, {
                                mode,
                                type: 'user',
                                errors: validation.errors
                            });

                            logger.auth.error('❌ Données utilisateur invalides:', validation.errors);
                            return;
                        }

                        // Mettre à jour l'état d'authentification
                        runInAction(() => {
                            this.userInfo = {
                                id: authData.userId,
                                email: authData.email,
                                fullName: authData.firstName || authData.lastName
                                    ? `${authData.firstName || ''} ${authData.lastName || ''}`.trim()
                                    : authData.email.split('@')[0], // Nom plus riche si disponible
                                firstName: authData.firstName || '',
                                lastName: authData.lastName || '',
                                authMode: mode
                            };
                        });
                        logger.auth.debug(`✅ Utilisateur authentifié via API (${mode}):`, this.userInfo);

                        trackAuthEvent(AUTH_EVENT_TYPES.USER_AUTH_SUCCESS, {
                            mode,
                            userId: authData.userId
                        });
                    } else {
                        logger.auth.debug(`🔒 Utilisateur non authentifié via API (${mode})`);

                        trackAuthEvent(AUTH_EVENT_TYPES.USER_AUTH_FAILURE, {
                            mode,
                            reason: 'not_authenticated',
                            hasUserId: !!authData?.userId,
                            hasEmail: !!authData?.email,
                            isAuthenticated: !!authData?.isAuthenticated
                        });
                    }
                } catch (parseError) {
                    logger.auth.warn('⚠️ Erreur lors du parsing JSON, probablement pas une réponse JSON:', parseError);
                    logger.auth.debug('⚠️ Ce comportement est normal en l\'absence d\'authentification ou en mode iframe');
                }
            } else {
                // Tracer l'erreur API
                trackAuthEvent(AUTH_EVENT_TYPES.API_ERROR, {
                    mode,
                    type: 'user',
                    status: response.status,
                    url: apiUrl
                });

                logger.auth.warn(`⚠️ API auth-info (${apiUrl}) non disponible ou erreur:`, response.statusText);
                // Essayer de lire les détails de l'erreur
                try {
                    const errorText = await response.text();
                    if (errorText) {
                        logger.auth.warn('Détails:', errorText.substring(0, 150) + (errorText.length > 150 ? '...' : ''));
                    }
                } catch {
                    // Ignorer les erreurs supplémentaires
                }
            }
        } catch (fetchError) {
            // Tracer l'erreur d'exception
            trackAuthEvent(AUTH_EVENT_TYPES.USER_AUTH_FAILURE, {
                mode,
                reason: 'fetch_exception',
                error: fetchError.message
            });

            logger.auth.warn(`⚠️ Erreur lors de la requête d\'authentification (${apiUrl}):`, fetchError);
            logger.auth.debug('⚠️ Ce comportement peut être normal en l\'absence d\'API d\'authentification');
        }
    }

    /**
     * Prépare les en-têtes d'authentification standardisés pour les requêtes API
     * @returns {Object} Les en-têtes HTTP à utiliser
     */
    prepareAuthHeaders() {
        const headers = {};
        
        if (this.partnerInfo) {
            headers['X-Partner-ID'] = this.partnerInfo.id;
            headers['X-Partner-Token'] = this.partnerInfo.token;
            
            if (this.partnerInfo.email) {
                headers['X-User-Email'] = this.partnerInfo.email;
            }
        }
        
        if (this.userInfo) {
            headers['X-User-Id'] = this.userInfo.id;
            
            if (this.userInfo.email) {
                headers['X-User-Email'] = this.userInfo.email;
            }
        }
        
        // Ajouter l'en-tête de mode standardisé
        headers['X-Auth-Mode'] = this.authMode || 'unknown';
        
        return headers;
    }

    // Cette méthode est appelée lorsqu'un utilisateur tente d'accéder à une fonctionnalité protégée
    async accessFeature(onAuthenticated, onUnauthenticated) {
        logger.auth.debug('🔒 Tentative d\'accès à une fonctionnalité protégée');
        
        if (this.userInfo) {
            logger.auth.debug('✅ Accès autorisé pour l\'utilisateur:', this.userInfo.email);
            onAuthenticated(this.userInfo);
            return;
        }
        
        // Vérifier si nous sommes dans un iframe
        if (this.authMode === AUTH_MODES.IFRAME) {
            logger.auth.debug('📱 Mode iframe détecté, tentative de communication avec le parent');
            
            // Informer le site parent qu'une authentification est requise
            window.parent.postMessage({
                action: 'authenticationRequired',
                feature: 'protected_feature'
            }, '*');
            
            // Vérifier si des paramètres d'authentification sont présents
            if (contextStore.hasAuthParams()) {
                await this.authenticateFromIframeParams();
                if (this.userInfo) {
                    onAuthenticated(this.userInfo);
                    return;
                }
            } else {
                logger.auth.debug('⚠️ Iframe: Pas d\'informations d\'authentification dans l\'URL');
                if (onUnauthenticated) onUnauthenticated();
                return;
            }
        }
    
        logger.auth.debug('⚠️ Utilisateur non authentifié, affichage de l\'interface de connexion');
        this.showSignInForm(onUnauthenticated);
    }

    // Cette méthode montre une interface pour inviter l'utilisateur à s'authentifier
    showSignInForm(onUnauthenticated) {
        logger.auth.debug('🔑 Affichage du formulaire de connexion');

        // Rediriger vers la page d'authentification du partenaire courant.
        // - En mode custom hostname proxy : contextStore.loginUrl (injecté via cookie genealorama_partner)
        // - Fallback transitoire pour familystory.live legacy : familystory.live/auth
        // Sera nettoyé après la migration de familystory.live vers le mode custom hostname.
        const currentUrl = encodeURIComponent(window.location.href);
        logger.auth.debug('📌 URL actuelle pour le retour:', currentUrl);

        const redirectToAuth = confirm("Cette fonctionnalité nécessite une authentification. Souhaitez-vous vous connecter?");

        if (redirectToAuth) {
            const partnerLoginUrl = contextStore.loginUrl;
            const target = partnerLoginUrl
                ? `${partnerLoginUrl}${partnerLoginUrl.includes('?') ? '&' : '?'}returnUrl=${currentUrl}`
                : `https://familystory.live/auth?returnUrl=${currentUrl}`;
            logger.auth.debug('➡️ Redirection vers la page d\'authentification:', target);
            window.location.href = target;
        } else {
            logger.auth.debug('❌ Authentification annulée par l\'utilisateur');
            if (onUnauthenticated) {
                onUnauthenticated();
            }
        }
    }

    // Méthode pour vérifier si l'utilisateur est authentifié
    get isAuthenticated() {
        return !!this.userInfo;
    }
    
    // Getter pour vérifier si le partenaire est authentifié
    get isPartnerAuthenticated() {
        return !!this.partnerInfo && !!this.partnerInfo.id;
    }
}

const authStore = new AuthStore();
export default authStore;